import React from "react";
import {Button, IconButton} from "@mui/material";
import {useAuth} from "../../utils/hooks/useAuth";
import {useNavigate} from "react-router-dom";
import {useNotification} from "../../utils/hooks/useNotification";
import {useConfirm} from "material-ui-confirm";
import buildUrl from "../../utils/api/buildUrl";
import useSWR from "swr";
import {deleteFetcher, getFetcher} from "../../utils/api/fetchers";
import UserRole from "../../schemas/user/roles";
import EntityList from "../../components/list/EntityList";
import InfoIcon from "@mui/icons-material/Info";
import SailingIcon from "@mui/icons-material/Sailing";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import {PaginatedClubs} from "../../schemas/club/clubSchema";
import useTableParams from "../../components/list/useTableParams";

const Clubs: React.FC = () => {
    const {hasPermission} = useAuth();
    const {sortKeys, sortDirections, page, rowsPerPage, filters, setPage} = useTableParams();
    const navigate = useNavigate();
    const {showNotification} = useNotification();
    const confirm = useConfirm();

    const url = buildUrl('/clubs', {
        page,
        size: rowsPerPage,
        sortBy: sortKeys,
        sortDirections,
        filters
    });
    const {data, mutate} = useSWR<PaginatedClubs>(url, getFetcher, {keepPreviousData: true});
    const editPermission = hasPermission(UserRole.COMMODORE);

    const handleCreateClub = () => {
        navigate('/clubs/create');
    }

    const handleDetails = (clubId: number) => {
        navigate(`/clubs/${clubId}`);
    }

    const handleDelete = (clubId: number) => {
        confirm({
            description: `Opravdu chcete smazat tento klub?`,
            title: "Smazat klub",
            confirmationText: "Smazat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                deleteFetcher(`/clubs/${clubId}`).then(() => {
                    showNotification('Klub byl smazán', 'success');
                    if (data?.content.length === 1 && data.page.number > 0) {
                        setPage(data.page.number - 1);
                    } else {
                        mutate();
                    }
                }).catch(() => {
                    showNotification('Klub se nepodařilo smazat', 'error');
                });
            })
            .catch(() => {
            });
    }

    const handleBoats = (clubId: number) => {
        navigate(`/boats?default_filter:club=${data?.content.find(c => c.id === clubId)?.name}`);
    }

    return (
        <EntityList
            title="Kluby"
            controls={editPermission ? [
                <Button
                    key="add"
                    variant="contained"
                    color="success"
                    onClick={handleCreateClub}
                >
                    Vytvořit klub
                </Button>
            ] : []}
            headers={[
                {key: 'name', label: 'Název', sortable: true, filtered: true},
                {
                    key: 'controls', render: (club) => (
                        <div>
                            {!editPermission && (
                                <IconButton color="info" title="Detaily"
                                            onClick={() => handleDetails(club.id)}>
                                    <InfoIcon/>
                                </IconButton>
                            )}
                            <IconButton color="info" title="Lodě v klubu"
                                        onClick={() => handleBoats(club.id)}>
                                <SailingIcon/>
                            </IconButton>
                            {editPermission && (
                                <>
                                    <IconButton color="info" title="Detaily a úprava"
                                                onClick={() => handleDetails(club.id)}>
                                        <EditIcon/>
                                    </IconButton>
                                    <IconButton color="error" title="Smazat"
                                                onClick={() => handleDelete(club.id)}>
                                        <DeleteIcon/>
                                    </IconButton>
                                </>
                            )}
                        </div>
                    )
                }
            ]}
            entities={data?.content ?? []}
            entitiesCount={data?.page.totalElements ?? 0}
            indexes
        />
    );
}

export default Clubs;